import { Box, Flex, Spinner, Text } from '@chakra-ui/react';
import {
  AlertTriangle,
  CheckCircle2,
  FileUp,
  RotateCcw,
  X,
} from 'lucide-react';
import { useRef, useState, type ReactNode } from 'react';
import { Button } from './components/Button';
import { ProgressWithLabel } from './components/Progress';

/** Full-height centered screen (connecting / fatal errors). */
export function CenteredScreen({ children }: { children: ReactNode }) {
  return (
    <Flex
      direction="column"
      align="center"
      justify="center"
      gap="3"
      h="100%"
      px="6"
      textAlign="center"
    >
      {children}
    </Flex>
  );
}

export function ConnectingScreen() {
  return (
    <CenteredScreen>
      <Spinner color="figma.accent" size="md" />
      <Text fontSize="sm" color="figma.textSecondary">
        Connecting to Figma…
      </Text>
    </CenteredScreen>
  );
}

export function ConnectionErrorScreen({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}) {
  return (
    <CenteredScreen>
      <Box color="figma.warning">
        <AlertTriangle size={28} />
      </Box>
      <Text fontSize="sm" color="figma.textSecondary" maxW="320px">
        {message}
      </Text>
      <Button colorPalette="gray" variant="outline" onClick={onRetry}>
        Try again
      </Button>
    </CenteredScreen>
  );
}

export function Header({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <Box
      as="header"
      px="4"
      py="3"
      borderBottomWidth="1px"
      borderColor="figma.border"
      bg="figma.bgDark"
    >
      <Text fontSize="md" fontWeight="600" color="figma.text" letterSpacing="snug">
        {title}
      </Text>
      <Text mt="0.5" fontSize="xs" color="figma.textSecondary">
        {subtitle}
      </Text>
    </Box>
  );
}

export function Footer({ label }: { label: string }) {
  return (
    <Box
      as="footer"
      px="4"
      py="2"
      borderTopWidth="1px"
      borderColor="figma.border"
      bg="figma.bgDark"
    >
      <Text fontSize="2xs" color="figma.textMuted">
        {label}
      </Text>
    </Box>
  );
}

/** Sticky error/notice banner shown above the content. */
export function Banner({
  text,
  tone = 'error',
  onDismiss,
}: {
  text: string;
  tone?: 'error' | 'warning';
  onDismiss?: () => void;
}) {
  const bg = tone === 'error' ? 'figma.errorMuted' : 'figma.warningMuted';
  const fg = tone === 'error' ? 'figma.error' : 'figma.warning';
  return (
    <Flex
      align="flex-start"
      gap="2"
      px="4"
      py="2.5"
      bg={bg}
      borderBottomWidth="1px"
      borderColor="figma.border"
    >
      <Box color={fg} mt="0.5" flexShrink={0}>
        <AlertTriangle size={14} />
      </Box>
      <Text flex="1" fontSize="xs" color="figma.text">
        {text}
      </Text>
      {onDismiss && (
        <Box
          as="button"
          color="figma.textSecondary"
          _hover={{ color: 'figma.text' }}
          onClick={onDismiss}
          flexShrink={0}
        >
          <X size={14} />
        </Box>
      )}
    </Flex>
  );
}

export function UploadScreen({
  accept,
  dropHint,
  subhint,
  onFile,
}: {
  accept: string;
  dropHint: string;
  subhint?: string | undefined;
  onFile: (file: File) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  return (
    <Box h="100%" p="4">
      <Flex
        as="button"
        onClick={() => inputRef.current?.click()}
        onDragOver={(e: React.DragEvent<HTMLElement>) => {
          e.preventDefault();
          setDragging(true);
        }}
        onDragLeave={(e: React.DragEvent<HTMLElement>) => {
          // Ignore dragleave bubbling up from inner children (icon/text), which
          // would otherwise strobe the highlight as the pointer crosses them.
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) {
            setDragging(false);
          }
        }}
        onDrop={(e: React.DragEvent<HTMLElement>) => {
          e.preventDefault();
          setDragging(false);
          const file = e.dataTransfer?.files[0];
          if (file) onFile(file);
        }}
        direction="column"
        align="center"
        justify="center"
        gap="3"
        h="100%"
        w="full"
        px="6"
        borderWidth="1.5px"
        borderStyle="dashed"
        borderColor={dragging ? 'figma.accent' : 'figma.borderStrong'}
        bg={dragging ? 'figma.accentMuted' : 'transparent'}
        borderRadius="lg"
        cursor="pointer"
        transition="all 0.15s"
        _hover={{ borderColor: 'figma.accent' }}
      >
        <Flex
          align="center"
          justify="center"
          w="56px"
          h="56px"
          borderRadius="full"
          bg="figma.panel"
          color={dragging ? 'figma.accent' : 'figma.textSecondary'}
        >
          <FileUp size={24} />
        </Flex>
        <Text fontSize="xl" fontWeight="600" color="figma.text" letterSpacing="snug">
          {dropHint}
        </Text>
        {subhint && (
          <Text fontSize="sm" color="figma.textSecondary" maxW="280px" lineHeight="snug">
            {subhint}
          </Text>
        )}
        <Text fontSize="sm" fontWeight="500" color="figma.accent">
          Choose a file
        </Text>
      </Flex>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => {
          // currentTarget, not target: Preact types `target` as a bare
          // EventTarget, and it's the input either way — `target` can be a
          // descendant, `currentTarget` is what the handler is on.
          const input = e.currentTarget;
          const file = input.files?.[0];
          if (file) onFile(file);
          input.value = '';
        }}
      />
    </Box>
  );
}

export function ProcessingScreen({
  fileName,
  message,
  progress,
  onCancel,
}: {
  fileName: string;
  message: string | null;
  progress: { completed: number; total: number } | null;
  onCancel: () => void;
}) {
  const pct =
    progress && progress.total > 0
      ? (progress.completed / progress.total) * 100
      : 0;
  const label =
    progress && progress.total > 0
      ? `${progress.completed} / ${progress.total}`
      : (message ?? 'Working…');

  return (
    <Flex direction="column" justify="center" gap="4" h="100%" p="5">
      <Box>
        <Text fontSize="sm" fontWeight="600" color="figma.text" truncate>
          {fileName}
        </Text>
        <Text fontSize="xs" color="figma.textSecondary" mt="0.5">
          {message ?? 'Importing…'}
        </Text>
      </Box>
      <ProgressWithLabel
        value={pct}
        label={label}
        showPercent={!!progress && progress.total > 0}
      />
      <Flex justify="flex-end">
        <Button colorPalette="gray" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </Flex>
    </Flex>
  );
}

export function SuccessScreen({
  message,
  onReset,
}: {
  message: string;
  onReset: () => void;
}) {
  return (
    <CenteredScreen>
      <Box color="figma.success">
        <CheckCircle2 size={32} />
      </Box>
      <Text fontSize="md" fontWeight="600" color="figma.text">
        Imported into Figma
      </Text>
      <Text fontSize="xs" color="figma.textSecondary" maxW="320px">
        {message}
      </Text>
      <Button colorPalette="accent" onClick={onReset}>
        <RotateCcw size={14} /> Import another
      </Button>
    </CenteredScreen>
  );
}

export function ErrorScreen({
  message,
  onReset,
}: {
  message: string;
  onReset: () => void;
}) {
  return (
    <CenteredScreen>
      <Box color="figma.error">
        <AlertTriangle size={28} />
      </Box>
      <Text fontSize="sm" color="figma.textSecondary" maxW="320px">
        {message}
      </Text>
      <Button colorPalette="gray" variant="outline" onClick={onReset}>
        Start over
      </Button>
    </CenteredScreen>
  );
}

/** The settings screen: file name bar + app settings + Import action. */
export function SettingsScreen({
  fileName,
  settings,
  importLabel,
  onChangeFile,
  onImport,
}: {
  fileName: string;
  settings: ReactNode;
  importLabel: string;
  onChangeFile: () => void;
  onImport: () => void;
}) {
  return (
    <Flex direction="column" h="100%" minH="0">
      <Flex
        align="center"
        justify="space-between"
        gap="2"
        px="4"
        py="2.5"
        borderBottomWidth="1px"
        borderColor="figma.border"
      >
        <Text fontSize="xs" color="figma.text" fontWeight="500" truncate>
          {fileName}
        </Text>
        <Button
          colorPalette="gray"
          variant="ghost"
          size="xs"
          onClick={onChangeFile}
        >
          Change
        </Button>
      </Flex>
      <Box flex="1" minH="0" overflowY="auto" px="4" py="3">
        {settings}
      </Box>
      <Box
        px="4"
        py="3"
        borderTopWidth="1px"
        borderColor="figma.border"
        bg="figma.bgDark"
      >
        <Button colorPalette="accent" w="full" onClick={onImport}>
          {importLabel}
        </Button>
      </Box>
    </Flex>
  );
}
